import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { projectsApi, layersApi, type Layer } from '@/api/mapApi';
import { useAuthStore } from '@/stores/authStore';
import { ApiError } from '@/api/client';
import { ShareModal } from '@/components/map/ShareModal';

interface Project {
  id: string;
  name: string;
  description: string | null;
  center_lat: number;
  center_lng: number;
  zoom_level: number;
  is_public: number;
  owner_id: string;
  share_token: string | null;
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [project, setProject] = useState<Project | null>(null);
  const [layers, setLayers] = useState<Layer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingLayer, setEditingLayer] = useState<Layer | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (id) loadAll();
  }, [id]);

  async function loadAll() {
    if (!id) return;
    setLoading(true);
    try {
      const [p, l] = await Promise.all([
        projectsApi.get(id),
        layersApi.listForProject(id),
      ]);
      setProject(p as unknown as Project);
      setLayers(l.items);
    } catch (err) {
      if (err instanceof ApiError) console.error(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePublishToggle() {
    if (!project) return;
    try {
      const res = await projectsApi.publish(project.id);
      setProject({ ...project, is_public: res.is_public ? 1 : 0 });
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  }

  async function handleShare() {
    if (!project) return;
    setShowShareModal(true);
  }

  async function handleUpload(files: FileList | null) {
    if (!files || !project) return;
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(
          `${import.meta.env.VITE_API_BASE || ''}/api/projects/${project.id}/layers`,
          {
            method: 'POST',
            body: fd,
            credentials: 'include',
            headers: { Authorization: `Bearer ${useAuthStore.getState().accessToken}` },
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Upload thất bại');
        }
      } catch (err) {
        alert(`Upload ${file.name} thất bại: ${err instanceof Error ? err.message : 'Unknown'}`);
      }
    }
    loadAll();
  }

  async function handleDeleteLayer(layer: Layer) {
    if (!confirm(`Xóa layer "${layer.name}"?`)) return;
    try {
      await layersApi.delete(layer.id);
      loadAll();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  }

  async function handleToggleLayer(layer: Layer) {
    try {
      await layersApi.update(layer.id, { isVisible: !layer.isVisible });
      loadAll();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  }

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">Đang tải...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="h-full flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-500 mb-4">Không tìm thấy đồ án</p>
            <Link to="/admin/projects" className="text-brand-600 hover:underline">
              ← Quay lại danh sách
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isOwner = project.owner_id === user?.id || user?.role === 'super_admin';

  return (
    <div className="h-full flex flex-col">
      <Header />
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="max-w-4xl mx-auto p-4">
          <Link to="/admin/projects" className="text-sm text-gray-600 hover:text-gray-900 mb-2 inline-block">
            ← Quay lại danh sách
          </Link>

          <div className="bg-white rounded-lg p-6 mb-4">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
                {project.description && (
                  <p className="text-sm text-gray-600 mt-1">{project.description}</p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Center: {project.center_lat.toFixed(4)}, {project.center_lng.toFixed(4)} · Zoom {project.zoom_level}
                </p>
              </div>
              <span
                className={`px-2 py-1 text-xs rounded ${
                  project.is_public ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {project.is_public ? 'Công khai' : 'Riêng tư'}
              </span>
            </div>

            {isOwner && (
              <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={handlePublishToggle}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  {project.is_public ? 'Ẩn đồ án' : 'Công khai'}
                </button>
                <button
                  onClick={handleShare}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
                >
                  🔗 Tạo link chia sẻ
                </button>
              </div>
            )}

          </div>

          {/* Layers section */}
          <div className="bg-white rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Layers ({layers.length})</h2>
              {isOwner && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-brand-600 text-white px-3 py-1.5 rounded text-sm hover:bg-brand-700"
                >
                  + Upload KMZ/KML
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".kmz,.kml"
                multiple
                onChange={(e) => handleUpload(e.target.files)}
                className="hidden"
              />
            </div>

            {layers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">
                Chưa có layer nào. Upload file KMZ/KML để bắt đầu.
              </p>
            ) : (
              <ul className="space-y-2">
                {layers.map((l) => (
                  <li
                    key={l.id}
                    className="border border-gray-200 rounded-md p-3 flex items-center gap-3"
                  >
                    <span
                      className="w-4 h-4 rounded-sm border border-gray-300"
                      style={{ backgroundColor: l.style.fillColor ?? '#3388ff' }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{l.name}</p>
                      <p className="text-xs text-gray-500">
                        {l.fileType.toUpperCase()} · {l.bbox ? 'Có bbox' : 'Chưa parse'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleLayer(l)}
                      className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      {l.isVisible ? '👁️ Hiện' : '🙈 Ẩn'}
                    </button>
                    {isOwner && (
                      <>
                        <button
                          onClick={() => setEditingLayer(l)}
                          className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                        >
                          ✏️ Style
                        </button>
                        <button
                          onClick={() => handleDeleteLayer(l)}
                          className="text-xs px-2 py-1 text-red-600 border border-red-200 rounded hover:bg-red-50"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {editingLayer && (
        <LayerStyleEditor
          layer={editingLayer}
          onClose={() => setEditingLayer(null)}
          onSaved={() => {
            setEditingLayer(null);
            loadAll();
          }}
        />
      )}

      {showShareModal && project && (
        <ShareModal
          projectId={project.id}
          projectName={project.name}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
}

function LayerStyleEditor({ layer, onClose, onSaved }: { layer: Layer; onClose: () => void; onSaved: () => void }) {
  const [style, setStyle] = useState(layer.style);
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await layersApi.update(layer.id, { style });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold mb-4">Style: {layer.name}</h2>
        <div className="space-y-3">
          <ColorField label="Màu viền" value={style.strokeColor ?? '#3388ff'} onChange={(v) => setStyle({ ...style, strokeColor: v })} />
          <SliderField label="Độ dày viền" min={1} max={10} value={style.strokeWeight ?? 2} onChange={(v) => setStyle({ ...style, strokeWeight: v })} unit="px" />
          <SliderField label="Độ trong suốt viền" min={0} max={1} step={0.05} value={style.strokeOpacity ?? 1} onChange={(v) => setStyle({ ...style, strokeOpacity: v })} />
          <ColorField label="Màu fill" value={style.fillColor ?? '#3388ff'} onChange={(v) => setStyle({ ...style, fillColor: v })} />
          <SliderField label="Độ trong suốt fill" min={0} max={1} step={0.05} value={style.fillOpacity ?? 0.2} onChange={(v) => setStyle({ ...style, fillOpacity: v })} />
        </div>
        <div className="flex gap-2 justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
            Hủy
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-brand-600 text-white rounded hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'Đang lưu...' : 'Lưu'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-12 h-9 rounded border" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono"
        />
      </div>
    </div>
  );
}

function SliderField({ label, min, max, step = 1, value, onChange, unit = '' }: { label: string; min: number; max: number; step?: number; value: number; onChange: (v: number) => void; unit?: string }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <span className="text-sm text-gray-600">{value}{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full"
      />
    </div>
  );
}
